import { z } from 'zod';

export const WeatherConditionSchema = z.enum(['Rainy', 'Cloudy', 'Sunny']);
export type WeatherCondition = z.infer<typeof WeatherConditionSchema>;

export const DrainStatusSchema = z.enum(['No Water', 'Some Water visible', 'Overflowing']);
export type DrainStatus = z.infer<typeof DrainStatusSchema>;

export const OwnershipNoticeSchema = z.enum(['Yes', 'No']);
export type OwnershipNotice = z.infer<typeof OwnershipNoticeSchema>;

export const WasteDumpingSchema = z.enum(['Yes', 'No']);
export type WasteDumping = z.infer<typeof WasteDumpingSchema>;

export const VegetationStatusSchema = z.enum([
  'No Vegetation',
  'Some Vegetation',
  'Covered with Vegetation',
]);
export type VegetationStatus = z.infer<typeof VegetationStatusSchema>;

export const WaterloggedStatusSchema = z.enum([
  'No Water in Site',
  'Some Water spots spread across site',
  'Covered in Water',
]);
export type WaterloggedStatus = z.infer<typeof WaterloggedStatusSchema>;

export const RoadTypeSchema = z.enum(['Mud Road', 'Cement Road', 'Tar Road']);
export type RoadType = z.infer<typeof RoadTypeSchema>;

export const RatingSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type Rating = z.infer<typeof RatingSchema>;

export const RoadConditionSchema = z.object({
  type: RoadTypeSchema,
  rating: RatingSchema,
});
export type RoadCondition = z.infer<typeof RoadConditionSchema>;

export const ConstructionActivitySchema = z.enum(['None', 'Active Construction Nearby']);
export type ConstructionActivity = z.infer<typeof ConstructionActivitySchema>;

export const IssueSchema = z.object({
  title: z.string(),
  severity: z.enum(['low', 'medium', 'high']),
  remarks: z.string(),
});
export type Issue = z.infer<typeof IssueSchema>;

export const MediaItemSchema = z.object({
  id: z.string(),
  type: z.enum(['photo', 'video']),
  label: z.string().optional(),
  url: z.string(),
  durationSec: z.number().optional(),
  propertyId: z.string(),
  inspectionId: z.string(),
  gpsTagged: z.boolean().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
export type MediaItem = z.infer<typeof MediaItemSchema>;

function dataWithMediaSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.object({
    value: valueSchema,
    media: z.array(MediaItemSchema).optional(),
  });
}

export type DataWithMedia<T extends string> = {
  value: T;
  media?: MediaItem[];
};

export const InspectionStatusSchema = z.enum([
  'pending_review',
  'completed',
  'flagged',
  'rejected',
  'cancelled',
]);

const DataWithMediaSchemaFor = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.object({ value: valueSchema, media: z.array(MediaItemSchema).optional() });

export const LandInspectionSchema = z.object({
  inspectionId: z.string(),
  propertyId: z.string(),
  agentId: z.string(),
  visitDate: z.string(),
  inspector: z.string(),
  status: InspectionStatusSchema,
  summary: z.string(),
  registeredOwnerName: z.string(),
  detectedOwnerName: z.string().optional(),
  issues: z.array(IssueSchema).optional(),

  weather: WeatherConditionSchema,
  drainStatus: DataWithMediaSchemaFor(DrainStatusSchema),
  ownershipNotice: DataWithMediaSchemaFor(OwnershipNoticeSchema),
  wasteDumping: DataWithMediaSchemaFor(WasteDumpingSchema),
  vegetationStatus: DataWithMediaSchemaFor(VegetationStatusSchema),
  waterloggedStatus: DataWithMediaSchemaFor(WaterloggedStatusSchema),
  roadCondition: RoadConditionSchema,
  constructionActivity: DataWithMediaSchemaFor(ConstructionActivitySchema),

  mainAccessPoint: z.array(MediaItemSchema),
  siteOverview: z.array(MediaItemSchema),

  createdAt: z.string(),

  reviewNotes: z.string().optional(),
  reviewedBy: z.string().optional(),
  reviewedAt: z.string().optional(),
  propertyAddress: z.string().optional(),
  reviewTaskDueDate: z.string().optional(),
});

export type LandInspection = z.infer<typeof LandInspectionSchema>;

/** Response for GET /inspections/:id/pdf — a short-lived presigned download URL. */
export const ReportPdfResponseSchema = z.object({
  url: z.string().url(),
});
export type ReportPdfResponse = z.infer<typeof ReportPdfResponseSchema>;

export const ApproveInspectionRequestSchema = z.object({
  reviewNotes: z.string().optional(),
  // Reviewer may edit the report summary while approving or rejecting.
  summary: z.string().optional(),
});
export type ApproveInspectionRequest = z.infer<typeof ApproveInspectionRequestSchema>;

export const CreateInspectionRequestSchema = z.object({
  taskId: z.string(),
  registeredOwnerName: z.string(),
  detectedOwnerName: z.string().optional(),
  issues: z.array(IssueSchema).optional(),

  weather: WeatherConditionSchema,
  drainStatus: dataWithMediaSchema(DrainStatusSchema),
  ownershipNotice: dataWithMediaSchema(OwnershipNoticeSchema),
  wasteDumping: dataWithMediaSchema(WasteDumpingSchema),
  vegetationStatus: dataWithMediaSchema(VegetationStatusSchema),
  waterloggedStatus: dataWithMediaSchema(WaterloggedStatusSchema),
  roadCondition: RoadConditionSchema,
  constructionActivity: dataWithMediaSchema(ConstructionActivitySchema),

  mainAccessPoint: z.array(MediaItemSchema),
  siteOverview: z.array(MediaItemSchema),
});
export type CreateInspectionRequest = z.infer<typeof CreateInspectionRequestSchema>;
